import { Node, Edge } from 'reactflow';
import { WorkflowBaseService } from '../services/WorkflowBaseService';
import { AgentBaseService } from '@/features/Agents/services/agentBaseService';

/**
 * Interface para estender Node com a propriedade config que não está na definição original
 */
interface ExtendedNode extends Node {
  config?: Record<string, unknown>;
}

/**
 * Interface para o schema limpo do nó
 */
interface CleanNode {
  id: string;
  name: string;
  type: string;
  title: string;
  position?: { x: number; y: number }; // ✅ MANTER position para restauração
  nodeId?: string; // ✅ MANTER nodeId para identificação
  config: { [key: string]: unknown };
}

/**
 * Interface para o schema limpo da aresta
 */
interface CleanEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  metadata?: {
    condition?: 'true' | 'false';
    [key: string]: unknown;
  };
}

/**
 * Interface para o schema limpo do workflow
 */
export interface CleanWorkflowSchema {
  nodes: CleanNode[];
  edges: CleanEdge[];
  prompts?: Record<string, unknown>[];
  parameters?: { [key: string]: unknown };
  organizationId?: string;
  workspaceId?: string;
  workflow_base_id?: string;
}

/**
 * Limpa um valor da config, convertendo de {type, value} para o valor direto
 * 
 * Comportamentos especiais:
 * 1. Objetos com {type: "undefined"} são convertidos para objetos vazios {}
 * 2. Objetos com {type: X, value: Y} são convertidos para Y
 * 3. Arrays e objetos são processados recursivamente
 * 
 * @param value Valor a ser limpo
 * @returns Valor limpo
 */
const cleanConfigValue = (value: unknown): unknown => {
  // Se for null, undefined ou não for um objeto, retorna o valor como está
  if (!value || typeof value !== 'object') {
    return value;
  }
  
  // Se for um objeto com a propriedade type="undefined"
  if ('type' in value && value.type === 'undefined') {
    // Se tiver apenas a propriedade type ou type e outras propriedades vazias
    const allEmpty = Object.entries(value).every(([key, val]) => 
      key === 'type' || 
      val === null || 
      val === undefined || 
      (typeof val === 'object' && Object.keys(val).length === 0)
    );
    
    if (allEmpty) {
      return {}; // Retorna objeto vazio para substituir { type: "undefined" }
    }
  }
  
  // Se for um objeto com propriedades type e value, retorna apenas o value
  if ('type' in value && 'value' in value) {
    return value.value;
  }
  
  // Se for um objeto, limpa recursivamente cada propriedade
  if (!Array.isArray(value)) {
    const cleanedObject: { [key: string]: unknown } = {};
    
    for (const [key, val] of Object.entries(value)) {
      cleanedObject[key] = cleanConfigValue(val);
    }
    
    return cleanedObject;
  }
  
  // Se for um array, limpa cada elemento
  return value.map(item => cleanConfigValue(item));
}

/**
 * Limpa a configuração de um nó, convertendo propriedades no formato {type, value} para seus valores diretos
 * @param config Configuração original do nó
 * @returns Configuração limpa
 */
const cleanNodeConfig = (config: unknown): Record<string, unknown> => {
  if (!config || typeof config !== 'object') {
    return config as Record<string, unknown>;
  }
  
  const cleanedConfig: { [key: string]: unknown } = {};
  
  for (const [key, value] of Object.entries(config)) {
    // Pula campos que não queremos incluir no schema final
    // ✅ Removido 'position' da lista para preservar posições ao publicar
    if (key === 'testOutput' || key === 'data' || key === 'settings' || key === 'fields' || key === 'prompts') {
      continue;
    }
    
    cleanedConfig[key] = cleanConfigValue(value);
  }
  
  return cleanedConfig;
}

/**
 * Limpa um nó, removendo propriedades desnecessárias e transformando valores de configuração
 * @param node Nó original
 * @returns Nó limpo
 */
const cleanNode = (node: Node): CleanNode => {
  // Cast para ExtendedNode para acessar a propriedade config na raiz
  const extendedNode = node as ExtendedNode;
  
  // Garantir que temos uma cópia da config para trabalhar
  let nodeConfig = {};
  
  // Verificar todas as fontes possíveis de configuração
  
  // 1. Primeiro, verificar na raiz do objeto node (onde está config nos nós do editor)
  if (extendedNode.config && typeof extendedNode.config === 'object') {
    nodeConfig = { ...nodeConfig, ...extendedNode.config };
  }
  
  // 2. Verificar também dentro de node.data por outros campos úteis
  if (node.data) {
    // Se o node tiver uma configuração dentro de data
    if (node.data.config && typeof node.data.config === 'object') {
      nodeConfig = { ...nodeConfig, ...node.data.config };
    }
    
    // Se o node tiver campos de configuração diretamente em data
    if (node.data.settings && typeof node.data.settings === 'object') {
      nodeConfig = { ...nodeConfig, ...node.data.settings };
    }

    // Se o node tiver parâmetros de configuração específicos do tipo de nó
    if (node.data.parameters && typeof node.data.parameters === 'object') {
      nodeConfig = { ...nodeConfig, parameters: node.data.parameters };
    }
    
    // Campos comuns que geralmente fazem parte da configuração
    const configFields = [
      'url', 'method', 'headers', 'body', 'params', 'path', 'template', 
      'assistantId', 'prompt', 'operation', 'fileType', 'api_key', 'apiKey',
      'model', 'maxTokens', 'temperature', 'webookUrl', 'inputSchema', 'outputSchema',
      'input_schema', 'output_schema', 'query', 'format', 'mapping'
    ];
    
    // Extrair esses campos de data se eles existirem
    configFields.forEach(field => {
      if (field in node.data && node.data[field] !== undefined) {
        nodeConfig = { ...nodeConfig, [field]: node.data[field] };
      }
    });
  }
  
  // Se não houver nenhuma configuração, verificar se existe algum dado útil no nó
  if (Object.keys(nodeConfig).length === 0 && node.data) {
    // Copiar todos os campos úteis de data para config
    Object.entries(node.data).forEach(([key, value]) => {
      // Não incluir campos que são metadados ou que não são úteis para configuração
      // ✅ Removido 'position' da lista para preservar posições
      const skipFields = ['id', 'name', 'type', 'title', 'label', 
                         'testOutput', 'data', 'settings', 'workflowId', 'organizationId'];
      
      if (!skipFields.includes(key) && value !== undefined) {
        nodeConfig = { ...nodeConfig, [key]: value };
      }
    });
  }
  
  return {
    id: node.id,
    name: node.data.name || node.data.type,
    type: node.data.type,
    title: node.data.title || node.data.label,
    position: node.position, // ✅ Preservar position
    nodeId: node.data?.nodeId || node.data?.type, // ✅ Preservar nodeId
    config: cleanNodeConfig(nodeConfig)
  };
}

/**
 * Limpa uma aresta, removendo propriedades desnecessárias
 * @param edge Aresta original
 * @returns Aresta limpa
 */
const cleanEdge = (edge: Edge): CleanEdge => {
  const cleanedEdge: CleanEdge = {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: edge.type || 'default'
  };

  // Para edges que saem de nós do tipo "if" (sourceHandle é 'true' ou 'false'),
  // os metadados devem ter apenas o campo "condition"
  if (edge.sourceHandle === 'true' || edge.sourceHandle === 'false') {
    cleanedEdge.metadata = {
      condition: edge.sourceHandle
    };
    
    return cleanedEdge;
  }
  
  // Para outros tipos de edges, processamos os metadados normalmente
  let metadata: Record<string, unknown> = {};
  
  // Verificar se existe a propriedade 'condition' nos dados da aresta
  if (edge.data?.condition) {
    metadata.condition = edge.data.condition;
  }
  
  // Verificar se existe metadata explícito na aresta
  if (edge.data?.metadata && typeof edge.data.metadata === 'object') {
    metadata = { ...metadata, ...edge.data.metadata };
  }
  
  // Verificar source handles especiais que podem indicar metadados adicionais
  if (edge.sourceHandle) {
    // Para nós de loop, o sourceHandle indica loop/exit
    if (edge.sourceHandle === 'loop' || edge.sourceHandle === 'exit') {
      metadata.loopDirection = edge.sourceHandle;
      metadata.sourceHandle = edge.sourceHandle;
    } else {
      // Para outros tipos de sourceHandle, adicionar como metadado para referência
      metadata.sourceHandle = edge.sourceHandle;
    }
  }
  
  // Verificar dados específicos para outras edges
  if (edge.data?.sourceNodeId) {
    metadata.sourceNodeId = edge.data.sourceNodeId;
  }
  
  // Adicionar metadados apenas se houver algo para adicionar
  if (Object.keys(metadata).length > 0) {
    cleanedEdge.metadata = metadata;
  }

  return cleanedEdge;
}

/**
 * Limpa o schema do workflow para envio ao servidor
 * @param nodes Lista de nós
 * @param edges Lista de arestas
 * @param options Opções adicionais como organizationId e workspaceId
 * @returns Schema limpo do workflow
 */
export const cleanWorkflowSchema = async (
  nodes: Node[], 
  edges: Edge[],
  options?: { organizationId?: string; workspaceId?: string; workflowBaseId?: string }
): Promise<CleanWorkflowSchema> => {
  // Mapear nós para nova estrutura sem dados desnecessários
  const cleanedNodes = nodes.map(cleanNode);

  // Mapear arestas para nova estrutura
  const cleanedEdges = edges.map(cleanEdge);

  // Buscar parâmetros do agente associado ao workflow
  let agentParameters: { [key: string]: unknown } = {};
  
  // Identificar workflowId a partir dos dados dos nós
  let workflowId = options?.workflowBaseId || '';
  for (const node of nodes) {
    if (!workflowId && node.data?.workflowId) {
      workflowId = node.data.workflowId;
      break;
    }
  }
  
  if (workflowId) {
    try {
      // Buscar workflow para obter agent_base_id
      const workflowData = await WorkflowBaseService.getById(workflowId);
      const agentId = workflowData?.agent_base_id;
      
      if (agentId) {
        // Buscar settings do agente
        const { data: settings, error } = await AgentBaseService.getSettingsById(agentId);
        
        const parametersField = settings?.parameters as { values?: Record<string, unknown> } | undefined;
        if (!error && settings && parametersField && parametersField.values) {
          agentParameters = parametersField.values;
        }
      }
    } catch (error) {
      // Em caso de falha ao buscar parâmetros, prossegue com schema sem parâmetros adicionais
    }
  }

  // Gerar estrutura completa para o resultado
  const result: CleanWorkflowSchema = {
    nodes: cleanedNodes,
    edges: cleanedEdges,
    // Inicializamos prompts como um array vazio
    // Os prompts reais serão adicionados na função startTest do WorkflowTest.ts
    // a partir dos dados do agente relacionado ao workflow
    prompts: [],
    // Incluir parâmetros obtidos do agente
    parameters: agentParameters,
    // Usa os valores de organizationId e workspaceId passados como parâmetros,
    // ou inicializa como strings vazias se não forem fornecidos
    organizationId: options?.organizationId || '',
    workspaceId: options?.workspaceId || '',
    workflow_base_id: options?.workflowBaseId || ''
  };

  if (workflowId) {
    result.workflow_base_id = workflowId;
  }
  return result;
};
